import { useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'
import { orderBy, zip } from 'lodash'
import { parseUnits } from 'viem'
import { mainnet } from 'viem/chains'

import { SupportedChainId } from '~/constants/chains'
import { useMultipleSmartAccountBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  TokenMetadataWithPrice,
  useMultipleTokensMetadata,
  useTokenMetadata,
} from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { getVaultAssetsQuery } from '~/hooks/useTokens'
import { IAddress } from '~/types'
import { isSwapChainId } from '~/utils'
import { TokenBalance } from '~/utils/account'
import { Address, EvmAddress } from '~/utils/address'
import { isStablecoin } from '~/utils/addresses'
import { getBigNumberFromNumber, getNumberFromBigNumber } from '~/utils/numbers'
import { getVaultAsset, isVaultAssetPair, isVaultToken } from '~/utils/vault'

export const useAutoSwap = ({
  sender,
  destinationToken,
  preventBridge = false,
}: {
  sender?: IAddress
  destinationToken?: {
    token: IAddress
    chainId: SupportedChainId
    amount: string
  }
  preventBridge?: boolean
}) => {
  const { convertTokenToUSD } = useTokenPrices()

  const { smartAccounts } = useSmartAccounts()
  const smartAccount = smartAccounts.find(
    (account) => account.address === sender,
  )

  const { balances: balancesMap } = useMultipleSmartAccountBalances(
    smartAccount ? [smartAccount.address] : [],
  )
  const balances = useMemo(
    () =>
      smartAccount ? (balancesMap[smartAccount.address]?.balances ?? []) : [],
    [balancesMap, smartAccount],
  )

  const destinationTokenMetadata = useTokenMetadata({
    address: destinationToken?.token,
    chainId: destinationToken?.chainId,
  })

  const buyAmount = parseUnits(
    destinationToken?.amount ?? '0',
    destinationTokenMetadata?.decimals ?? 18,
  )

  const buyUSDValue = destinationToken
    ? convertTokenToUSD({
        token: destinationToken.token,
        amount: buyAmount,
        chainId: destinationToken.chainId,
      })
    : undefined

  const tokens = useMultipleTokensMetadata(balances, { includePrice: true })

  // What each direct-withdrawal source redeems for on-chain. The cached share
  // price is rounded down, so pricing a send at or near the full Earn balance
  // (as Send's Earn MAX does) needs slightly more shares than the account holds.
  const directWithdrawalBalances = useMemo(
    () =>
      balances.filter((balance) =>
        isDirectVaultWithdrawal(balance, destinationToken),
      ),
    [balances, destinationToken],
  )
  const redeemableAmounts = useQueries({
    queries: directWithdrawalBalances.map((balance) =>
      getVaultAssetsQuery({
        vault: balance.address,
        chainId: balance.chainId,
        balance: balance.amount,
      }),
    ),
    combine: selectQueryData,
  })

  const isDestinationVaultToken = destinationToken
    ? isVaultToken(destinationToken?.chainId, destinationToken?.token)
    : false

  return useMemo(() => {
    // If USD value not available yet, return empty lists
    if (buyUSDValue === undefined)
      return { swapTokens: [], insufficientTokens: [] }

    const getRedeemable = (vault: IAddress) =>
      zip(directWithdrawalBalances, redeemableAmounts).find(
        ([direct]) => !!direct && Address.equal(direct.address, vault),
      )?.[1]

    const candidates = zip(balances, tokens)
      .map(([balance, token]) => {
        return {
          ...balance,
          metadata: token,
        }
      })
      .filter(
        (
          balance,
        ): balance is TokenBalance & { metadata: TokenMetadataWithPrice } =>
          !!balance.metadata?.price,
      )
      .filter((balance) =>
        balance.chainId === destinationToken?.chainId
          ? isSwapChainId(balance.chainId)
          : true,
      )
      // If prevent bridge is true, only include tokens on the destination chain
      .filter((balance) =>
        preventBridge ? balance.chainId === destinationToken?.chainId : true,
      )
      // If the destination token is a vault token, only include tokens on the destination chain
      .filter((balance) =>
        isDestinationVaultToken
          ? balance.chainId === destinationToken?.chainId
          : true,
      )
      // Exclude the destination token itself — paying for a token with the
      // same token on the same chain isn't a swap, it's a direct transfer.
      .filter(
        (balance) =>
          !(
            balance.chainId === destinationToken?.chainId &&
            Address.equal(balance.address, destinationToken?.token)
          ),
      )
      .map((balance) => {
        // Earn tokens redeem directly into their underlying asset via
        // ERC-4626 — there is no swap slippage on that path, so we skip the
        // cushion. Without this, sending the full displayed Earn balance is
        // rejected by a fraction of a percent.
        const isDirectWithdrawal = isDirectVaultWithdrawal(
          { address: balance.address, chainId: balance.chainId },
          destinationToken,
        )

        const sellEstimate = isDirectWithdrawal
          ? buyUSDValue / balance.metadata.price!
          : (buyUSDValue +
              buyUSDValue /
                getSellAmountSlippage(
                  {
                    address: destinationToken?.token ?? '',
                    chainId: destinationToken?.chainId ?? 1,
                    symbol: destinationTokenMetadata?.symbol,
                  },
                  {
                    address: balance.address,
                    chainId: balance.chainId,
                    symbol: balance.metadata.symbol,
                  },
                )) /
            balance.metadata.price!

        // Exact once the on-chain redeemable amount loads; until then, the
        // price estimate above.
        const redeemable = isDirectWithdrawal
          ? getRedeemable(balance.address)
          : undefined

        const sellEstimateBigNumber = redeemable
          ? getSharesForAssets({
              assets: buyAmount,
              shares: balance.amount,
              redeemable,
            })
          : getBigNumberFromNumber(sellEstimate, balance.metadata.decimals)

        return {
          ...balance,
          isDirectWithdrawal,
          sellEstimate: sellEstimateBigNumber,
        }
      })

    // Direct vault withdrawals use `<=` because there is no cushion to
    // protect against off-by-one — the user should be able to redeem
    // exactly their share balance. Other paths keep the strict `<` so the
    // slippage cushion stays a true safety margin.
    const hasSufficientBalance = (candidate: (typeof candidates)[number]) =>
      candidate.isDirectWithdrawal
        ? candidate.sellEstimate <= candidate.amount
        : candidate.sellEstimate < candidate.amount

    const destinationKind = destinationToken && {
      address: destinationToken.token,
      chainId: destinationToken.chainId,
      symbol: destinationTokenMetadata?.symbol,
    }

    const swapTokens = orderBy(
      candidates.filter(hasSufficientBalance),
      [
        // 1) Earn that withdraws straight into the destination token
        (t) => (t.isDirectWithdrawal ? 0 : 1),
        // 2) Other networks before Mainnet, where gas is generally higher
        (t) => (t.chainId === mainnet.id ? 1 : 0),
        // 3) The same kind of asset as the destination token, so the default
        // doesn't sell an asset the org didn't choose to spend
        (t) =>
          destinationKind &&
          isSameKind(
            {
              address: t.address,
              chainId: t.chainId,
              symbol: t.metadata.symbol,
            },
            destinationKind,
          )
            ? 0
            : 1,
        // 4) Vault tokens
        (t) => (isVaultToken(t.chainId, EvmAddress.format(t.address)) ? 0 : 1),
        // 5) Prefer cross-chain over same chain
        (t) => (t.chainId !== destinationToken?.chainId ? 0 : 1),
        // 6) Order by balance (higher first)
        (t) => t.usdValue ?? 0,
      ],
      ['asc', 'asc', 'asc', 'asc', 'asc', 'desc'],
    )

    // Held-but-short candidates, each with how much more of the token the send
    // would need (cushion included). Ranked by the USD value of that shortfall
    // so callers can surface the cheapest top-ups first.
    const insufficientTokens = orderBy(
      candidates
        .filter((candidate) => !hasSufficientBalance(candidate))
        .map((candidate) => ({
          ...candidate,
          shortfall: candidate.sellEstimate - candidate.amount,
        }))
        .filter((candidate) => candidate.shortfall > 0n),
      [
        (t) =>
          getNumberFromBigNumber(t.shortfall, t.metadata.decimals) *
          t.metadata.price!,
      ],
      ['asc'],
    )

    return { swapTokens, insufficientTokens }
  }, [
    balances,
    tokens,
    buyAmount,
    buyUSDValue,
    destinationTokenMetadata?.symbol,
    destinationToken,
    preventBridge,
    isDestinationVaultToken,
    directWithdrawalBalances,
    redeemableAmounts,
  ])
}

// Module-level so useQueries keeps the combined result stable between renders.
const selectQueryData = <T>(results: { data: T | undefined }[]) =>
  results.map((result) => result.data)

// The shares a withdrawal of `assets` burns, at the on-chain rate of `shares`
// redeeming for `redeemable`. Rounds up, so it stays within `shares` exactly
// when `assets <= redeemable`.
const getSharesForAssets = ({
  assets,
  shares,
  redeemable,
}: {
  assets: bigint
  shares: bigint
  redeemable: bigint
}) => (assets * shares + redeemable - 1n) / redeemable

/**
 * Whether the swap is a single-chain ERC-4626 redemption: vault → its own
 * underlying asset on the same chain. Used to bypass the slippage cushion
 * because `previewWithdraw` on a standard ERC-4626 vault is exact.
 *
 * `isVaultAssetPair` covers the "vault paired with this asset" lookup; we
 * add the chainId-equality guard the utility doesn't enforce.
 *
 * IMPORTANT: this assumes every entry in `VAULT_ASSET_MAPPING` is a
 * standard ERC-4626 vault with no withdrawal fee, no queue, and no
 * rebasing quirks. Aave's StaticATokenLM and the Morpho Earn wrappers (the
 * entries today) satisfy this. If a non-vanilla vault is added, gate the
 * bypass behind a per-vault flag instead.
 */
const isDirectVaultWithdrawal = (
  sellToken: { address: IAddress; chainId: SupportedChainId },
  destinationToken: { token: IAddress; chainId: SupportedChainId } | undefined,
): boolean => {
  if (!destinationToken || sellToken.chainId !== destinationToken.chainId)
    return false
  return isVaultAssetPair({
    chainId: sellToken.chainId,
    vault: sellToken.address,
    asset: destinationToken.token,
  })
}

type KindToken = {
  address: IAddress
  chainId: number
  symbol: string | undefined
}

// An Earn token counts as its underlying asset.
const isStablecoinOrEarn = ({ address, chainId }: KindToken) =>
  isStablecoin(getVaultAsset(chainId, address) ?? address, chainId)

const isEthOrWeth = (symbol: string | undefined) =>
  symbol === 'ETH' || symbol === 'WETH'

/**
 * Whether two tokens are the same kind of asset: both stablecoins, both ETH
 * or WETH, or sharing a symbol.
 */
const isSameKind = (a: KindToken, b: KindToken) =>
  (isStablecoinOrEarn(a) && isStablecoinOrEarn(b)) ||
  (isEthOrWeth(a.symbol) && isEthOrWeth(b.symbol)) ||
  (!!a.symbol && a.symbol === b.symbol)

const getSellAmountSlippage = (
  buyToken: { address: string; chainId: number; symbol: string | undefined },
  sellToken: { address: string; chainId: number; symbol: string | undefined },
) => {
  if (!buyToken.symbol || !sellToken.symbol) return 200 // default 0.5% when no symbol
  if (buyToken.symbol === sellToken.symbol) return 2000 // 0.05% for bridge between same assets
  if (
    isStablecoin(buyToken.address, buyToken.chainId) &&
    isStablecoin(sellToken.address, sellToken.chainId)
  )
    return 2000 // 0.05% for swaps between stables
  if (
    isStablecoin(buyToken.address, buyToken.chainId) &&
    sellToken.symbol === 'ETH'
  )
    return 2000 // 0.05% for swaps between stable coin and ETH
  if (
    buyToken.symbol === 'ETH' &&
    isStablecoin(sellToken.address, sellToken.chainId)
  )
    return 2000 // 0.05% for swaps between stable coin and ETH
  return 200 // default 0.5%
}
